import type {
  WorkspaceListEntry,
  WorkspaceMember,
  WorkspaceMessage,
  WorkspaceMessageType,
  WorkspaceSenderRole,
} from "@/lib/types";
import type { ReactionEmoji } from "@/lib/workspace/reactions";
import type { ThreadKey } from "@/lib/workspace/threads";
import type { AttachedFile, RootStorageEntry } from "@/lib/workspace/files";

/** A quest-level count with no specific tab (legacy fallback before migration 20260924100000). */
export const LEGACY_QUEST_KEY = "*";
export type ThreadUnreadCounts = Map<string /*questId*/, Map<ThreadKey, number>>;

export interface IWorkspaceService {
  /** All workspaces the user is a member of (for sidebar). */
  getMyWorkspaces(userId: string): Promise<WorkspaceListEntry[]>;

  /**
   * Open the workspace for a freshly created quest: add the creator as its
   * first member and seed the "waiting for a worker" system message.
   *
   * Resolves only once the membership row exists, because that row is what
   * every other workspace policy keys off (`is_workspace_member`) — a caller
   * that navigates into the workspace must not do so before it is true.
   * Rejects if the membership could not be written.
   */
  createWorkspace(questId: string, creatorId: string): Promise<void>;

  /** All members of a workspace. */
  getWorkspaceMembers(questId: string): Promise<WorkspaceMember[]>;

  /**
   * Adds a hired worker to the workspace. Called only after the on-chain
   * accept has resolved, so the hire is already real when this runs; a failed
   * row is logged by the adapter rather than thrown, because unwinding the
   * caller at that point would leave the UI contradicting the chain.
   */
  addWorkerMember(questId: string, userId: string): Promise<void>;

  /**
   * Send a message in a workspace. Returns the created message.
   *
   * `taskId`: which thread to post to. `null`/omitted lets the DB trigger
   * redirect to the writer's sole entitled task (or leave it in General when
   * that is ambiguous or there is none) — do not compute entitlement here.
   *
   * `toGroup`: the writer explicitly chose the quest group chat (General).
   * Sends `to_group = true` so the DB trigger skips its legacy redirect.
   * Must not be combined with a `taskId`.
   */
  sendMessage(
    questId: string,
    content: string,
    messageType?: WorkspaceMessageType,
    metadata?: Record<string, unknown>,
    senderRoleOverride?: WorkspaceSenderRole,
    taskId?: string | null,
    toGroup?: boolean,
  ): Promise<WorkspaceMessage>;

  /**
   * Paginated message history. Cursor is the `created_at` of the oldest loaded message.
   *
   * `taskId`: `null` selects the quest-wide General thread; a string selects
   * that task's thread; `undefined` is treated the same as `null` (General).
   */
  getMessages(
    questId: string,
    before?: string,
    limit?: number,
    taskId?: string | null,
  ): Promise<{ messages: WorkspaceMessage[]; nextCursor: string | null }>;

  /**
   * Every attachment on a message the caller can read in this quest, newest
   * first, across all threads they are entitled to (RLS does the scoping).
   * Complete, not capped: the adapter pages until the rows run out.
   * Feeds the Files tab, which cannot see chat/submission media by listing
   * storage because those live in subfolders.
   */
  getMessageAttachments(questId: string): Promise<AttachedFile[]>;

  /**
   * Files-panel uploads at the root of the quest's `quest-files` folder,
   * newest first. Subfolders (chat/, submissions/) are excluded; their files
   * come from getMessageAttachments.
   */
  listRootFiles(questId: string): Promise<RootStorageEntry[]>;

  /**
   * Deletes stored objects from the `quest-files` bucket by path. Throws on a
   * storage error; the submit path calls it as best-effort cleanup of uploads
   * whose submission never persisted.
   */
  removeFiles(paths: string[]): Promise<void>;

  /**
   * Stores a file in the `quest-files` bucket at `path`. Throws storage's own
   * error on a refusal, so callers can tell a rejected file type apart.
   */
  uploadFile(path: string, file: File): Promise<void>;

  /** A stored object's bytes from the `quest-files` bucket. Throws on a storage error. */
  downloadFile(path: string): Promise<Blob>;

  /** Get workers who expressed interest in a quest's tasks. */
  getApplicants(questId: string): Promise<Array<{ userId: string; displayName: string; avatarUrl: string | null; taskId: string; appliedAt: string }>>;

  /** Update the read cursor for the current user in a workspace. */
  updateReadCursor(questId: string): Promise<void>;

  /** Get unread message counts for multiple workspaces. */
  getUnreadCounts(
    questIds: string[],
  ): Promise<Map<string, number>>;

  /** Unread per thread for every workspace the caller is in (own + system messages excluded). */
  getThreadUnreadCounts(questIds: string[]): Promise<ThreadUnreadCounts>;

  /**
   * Mark one thread read for the caller, up to `readUpTo` (the newest message
   * the chat rendered, ISO timestamp) or now when omitted. Never throws;
   * failure leaves the badge until the next success.
   */
  markThreadRead(questId: string, thread: ThreadKey, readUpTo?: string): Promise<void>;

  /** Set (insert or replace) the current user's reaction on a message. */
  setReaction(messageId: string, emoji: ReactionEmoji): Promise<void>;

  /** Remove the current user's reaction from a message. */
  clearReaction(messageId: string): Promise<void>;
}
